import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload } from 'react-icons/fa';
import Choices from '../../../../components/Choices.js';
import AttachmentArea from './AttachmentArea.js';
import FileUploadUI from './FileUploadUI.js';
import * as menu from "../../../../assets/menus/menu.js";
import EditorJSRenderer from './EditorJSRenderer.js';
import QuestionValidationRules from './QuestionValidationRules.js';
import ValidationErrorDisplay from './ValidationErrorDisplay.js';
import { getDisabledAnswers } from '../utils/questionValidation.js';

/**
 * FormQuestion Component
 * 
 * Renders individual form questions with various input types and attachment capabilities.
 * Supports multiple question types including text, number, date/time, single/multiple choice,
 * vocabulary selection, and department/functional area selection.
 * 
 * @param {object} question - The question object containing configuration and data
 * @param {string} questionId - Unique identifier for the question
 * @param {number} sIndex - Section index (for display purposes)
 * @param {number} qIndex - Question index within the section
 * @param {object} formValues - Current form values state
 * @param {object} vocabularyItems - Available vocabulary items by vocabulary ID
 * @param {object} customVocabularyItems - Available custom vocabulary items by vocabulary ID
 * @param {Array} departmentsVoc - Available departments for selection
 * @param {Array} functionalAreaVocs - Available functional areas for selection
 * @param {function} getDescendants - Function to get child functional areas
 * @param {object} answerFiles - Uploaded files by question ID
 * @param {object} answerLinks - Added links by question ID
 * @param {object} answerObservations - Added observations by question ID
 * @param {object} answerTexts - Added texts by question ID
 * @param {object} activeAttachmentTab - Active attachment tab by question ID
 * @param {function} setActiveAttachmentTab - Function to set active attachment tab
 * @param {function} handleInputChange - Function to handle input value changes
 * @param {function} setFormValues - Function to update form values state
 * @param {function} setZoomImageUrl - Function to set image for zoom modal
 * @param {function} getLocalizedText - Function to get text in current language
 * @param {function} getUrlFromFile - Function to generate file download URL
 * @param {function} isImageFile - Function to check if file is an image
 * @param {function} renderAttachmentArea - Function to render attachment tabs
 * @param {function} renderFileUploadUI - Function to render file upload interface
 * @param {function} formatScore - Function to format score display
 * @param {object} colors - Color scheme for styling
 * @param {boolean} readOnly - Whether the question is in read-only mode
 */
const FormQuestion = ({
  question,
  questionId,
  sIndex,
  qIndex,
  formValues,
  vocabularyItems,
  customVocabularyItems,
  departmentsVoc,
  functionalAreaVocs,
  getDescendants,
  answerFiles,
  answerLinks,
  answerObservations,
  answerTexts,
  activeAttachmentTab,
  setActiveAttachmentTab,
  handleInputChange,
  setFormValues,
  setZoomImageUrl,
  getLocalizedText,
  getUrlFromFile,
  isImageFile,
  renderAttachmentArea,
  renderFileUploadUI,
  formatScore,
  colors,
  validationErrors = {},
  readOnly = false,
  showScores = false,
  isSimulation = false,
  isAnswerReadOnlyCollaborative = null // Function to check if specific answer is readonly
}) => {
  const { t } = useTranslation();
  
  // Initialize i18n labels for this component
  useEffect(() => {
    const i18n = menu.getI18n();
    
    // Italian translations
    i18n.addResourceBundle('it', 'labels', {
      department: 'Dipartimento',
      functionalArea: 'Area Funzionale',
      selectDepartment: 'Seleziona Dipartimento',
      selectFunctionalArea: 'Seleziona Area Funzionale',
      enterNumberAnswer: 'Inserisci numero',
      tableNoColumns: 'Nessuna colonna configurata per questa tabella',
      rowNumber: 'N.',
      tableAction: 'Azione',
      tableEmpty: 'Nessun dato nella tabella',
      addRow: 'Aggiungi Riga',
      select: 'Seleziona...'
    }, true, true);

    // English translations
    i18n.addResourceBundle('en', 'labels', {
      department: 'Department',
      functionalArea: 'Functional Area',
      selectDepartment: 'Select Department',
      selectFunctionalArea: 'Select Functional Area',
      enterNumberAnswer: 'Enter number',
      tableNoColumns: 'No columns configured for this table',
      rowNumber: 'No.',
      tableAction: 'Action',
      tableEmpty: 'No data in table',
      addRow: 'Add Row',
      select: 'Select...'
    }, true, true);
  }, []);
  
  // Extract question properties for easier access
  const questionText = getLocalizedText(question.questionText, `${t('question', { ns: 'labels' })} ${qIndex + 1}`);
  const isRequired = question.mandatory === true;

  /**
   * Renders attached files for the question
   * Displays images with zoom capability and other files with download links
   * 
   * @returns {JSX.Element|null} File display component or null if no files
   */
  const renderAttachedFiles = () => {
    if (!question.files || question.files.length === 0) return null;

    return (
      <div className="mt-2 mb-3">
        <ul className="list-group list-group-flush">
          {question.files.map((file, fIndex) => {
            const fileData = getUrlFromFile(file);
            return (
              <li key={`file-${questionId}-${file.Id}`} className="list-group-item py-2 px-2">
                {isImageFile(fileData.mimeType) ? (
                  // Render image files with zoom functionality
                  <div className="d-flex justify-content-center">
                    <div
                      className="card shadow-sm border rounded-3"
                      style={{ maxWidth: "100%", maxHeight: "320px" }}
                    >
                      <div className="p-2">
                        <img
                          src={fileData.url}
                          alt={file.Name || `Image ${fIndex + 1}`}
                          className="img-fluid rounded"
                          onClick={() => setZoomImageUrl(fileData.url)}
                          style={{
                            maxHeight: "300px",
                            objectFit: "contain",
                            width: "100%",
                            cursor: "zoom-in",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  // Render non-image files with download option
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <i className="fa-solid fa-paperclip me-2"></i>
                      {file.Name || `File ${fIndex + 1}`}
                      <span className="text-muted ms-2 small">
                        ({Math.round(file.Size / 1024)} KB)
                      </span>
                    </div>
                    <a
                      href={fileData.url}
                      download={file.Name}
                      className="btn btn-sm btn-outline-primary"
                    >
                      <FaDownload /> {t("download", { ns: "labels" })}
                    </a>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  /**
   * Renders a text input field for open-ended questions
   * 
   * @returns {JSX.Element} Text input component
   */
  const renderTextInput = () => (
    <input
      type="text"
      className="form-control"
      placeholder={t('enterAnswer', { ns: 'labels' })}
      required={isRequired}
      disabled={readOnly}
      value={formValues[questionId] || ''}
      onChange={(e) => handleInputChange(questionId, e.target.value)}
    />
  );

  /**
   * Renders a textarea for longer text responses
   * 
   * @returns {JSX.Element} Textarea component
   */
  const renderTextarea = () => (
    <textarea
      className="form-control"
      rows="3"
      placeholder={t('enterAnswer', { ns: 'labels' })}
      required={isRequired}
      disabled={readOnly}
      value={formValues[questionId] || ''}
      onChange={(e) => handleInputChange(questionId, e.target.value)}
    />
  );

  /**
   * Renders a number input field with validation for numeric-only input
   * 
   * @returns {JSX.Element} Number input component
   */
  const renderNumberInput = () => (
    <input
      type="text"
      className="form-control"
      placeholder={t('enterNumberAnswer', { ns: 'labels' })}
      required={isRequired}
      disabled={readOnly}
      value={formValues[questionId] || ''}
      onChange={(e) => {
        // Allow only numeric input (including leading zeros)
        const value = e.target.value;
        if (value === '' || /^\d+$/.test(value)) {
          handleInputChange(questionId, value);
        }
      }}
      onKeyPress={(e) => {
        // Prevent non-numeric characters from being typed
        if (!/\d/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'Enter') {
          e.preventDefault();
        }
      }}
    />
  );

  /**
   * Renders a date input field
   *
   * @returns {JSX.Element} Date input component
   */
  const renderDateInput = () => (
    <input
      type="date"
      className="form-control"
      placeholder={t('selectDate', { ns: 'labels' })}
      required={isRequired}
      disabled={readOnly}
      value={formValues[questionId] || ''}
      onChange={(e) => handleInputChange(questionId, e.target.value)}
    />
  );

  /**
   * Renders a time input field
   *
   * @returns {JSX.Element} Time input component
   */
  const renderTimeInput = () => (
    <input
      type="time"
      className="form-control"
      placeholder={t('selectTime', { ns: 'labels' })}
      required={isRequired}
      disabled={readOnly}
      value={formValues[questionId] || ''}
      onChange={(e) => handleInputChange(questionId, e.target.value)}
    />
  );

  /**
   * Renders a datetime-local input field
   *
   * @returns {JSX.Element} DateTime input component
   */
  const renderDateTimeInput = () => (
    <input
      type="datetime-local"
      className="form-control"
      placeholder={t('selectDateTime', { ns: 'labels' })}
      required={isRequired}
      disabled={readOnly}
      value={formValues[questionId] || ''}
      onChange={(e) => handleInputChange(questionId, e.target.value)}
    />
  );

  /**
   * Renders a period input (start/end) with optional time inclusion
   */
  const renderPeriodInput = () => {
    const includeTime = !!question.periodIncludeTime;
    const inputType = includeTime ? 'datetime-local' : 'date';
    const raw = formValues[questionId] || '';
    const [startVal, endVal] = typeof raw === 'string' && raw.includes('|') ? raw.split('|') : ['', ''];

    const toComparable = (val) => {
      if (!val) return null;
      try { return new Date(val).getTime(); } catch { return null; }
    };

    const updatePeriod = (nextStart, nextEnd) => {
      const s = toComparable(nextStart);
      const e = toComparable(nextEnd);
      if (s !== null && e !== null && s > e) {
        // Clamp end to start if invalid
        handleInputChange(questionId, `${nextStart}|${nextStart}`);
        return;
      }
      handleInputChange(questionId, `${nextStart || ''}|${nextEnd || ''}`);
    };

    return (
      <div className="mt-3">
        <div className="row g-2">
          <div className="col-6">
            <input
              type={inputType}
              className="form-control"
              value={startVal}
              disabled={readOnly}
              onChange={(e) => updatePeriod(e.target.value, endVal)}
              required={isRequired}
            />
          </div>
          <div className="col-6">
            <input
              type={inputType}
              className="form-control"
              value={endVal}
              disabled={readOnly}
              onChange={(e) => updatePeriod(startVal, e.target.value)}
              required={isRequired}
              min={!includeTime ? (startVal || undefined) : undefined}
            />
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renders radio buttons for single-choice questions
   * Filters out invisible answers and includes score display if available
   * 
   * @returns {JSX.Element|null} Radio buttons component or null if no answers
   */
  const renderRadioButtons = () => {
    if (!question.answers) return null;

    return (
      <div className="mt-3">
        {question.answers.filter(a => !a.invisible).map((answer, aIndex) => (
          <div key={`radio-${questionId}-${answer.answerId || answer.id}`} className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name={questionId}
              id={`radio-${questionId}-${answer.answerId || answer.id}`}
              required={isRequired}
              disabled={readOnly}
              value={answer.answerId || answer.id}
              checked={String(formValues[questionId]) === String(answer.answerId || answer.id)}
              onChange={(e) => handleInputChange(questionId, e.target.value)}
            />
            <label className="form-check-label" htmlFor={`radio-${questionId}-${answer.answerId || answer.id}`}>
              {getLocalizedText(answer.answerText, `${t('option', { ns: 'labels' })} ${aIndex + 1}`)}
              {showScores && formatScore && formatScore(answer.score) && (
                <span style={{ color: 'black', marginLeft: '10px' }}>
                  ({t('score', { ns: 'labels' })}: {formatScore(answer.score)})
                </span>
              )}
            </label>
          </div>
        ))}
      </div>
    );
  };

  /**
   * Renders checkboxes for multiple-choice questions
   * Manages array-based form values for multiple selections
   * 
   * @returns {JSX.Element|null} Checkboxes component or null if no answers
   */
  const renderCheckboxes = () => {
    if (!question.answers) return null;

    // Calculate if there's at least one editable answer (to show icon only for partial assignments)
    const visibleAnswers = question.answers.filter(a => !a.invisible);
    const hasAtLeastOneEditableAnswer = isAnswerReadOnlyCollaborative
      ? visibleAnswers.some(answer =>
          !isAnswerReadOnlyCollaborative(questionId, answer.answerId || answer.id)
        )
      : true; // If no collaborative function, all are editable

    return (
      <div className="mt-3">
        {visibleAnswers.map((answer, aIndex) => {
          // Check if this answer is currently selected
          const isChecked = Array.isArray(formValues[questionId]) &&
            formValues[questionId].includes(answer.answerId || answer.id);

          // Get disabled answers based on validation rules
          const disabledAnswers = getDisabledAnswers(question, formValues[questionId] || []);
          const isDisabledByValidation = disabledAnswers.includes(answer.answerId || answer.id);

          // Check if this specific answer is readonly in collaborative mode
          const isAnswerReadOnly = isAnswerReadOnlyCollaborative
            ? isAnswerReadOnlyCollaborative(questionId, answer.answerId || answer.id)
            : false;

          return (
            <div key={`checkbox-${questionId}-${answer.answerId || answer.id}`} className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={`checkbox-${questionId}-${answer.answerId || answer.id}`}
                name={`${questionId}[]`}
                value={answer.answerId || answer.id}
                checked={isChecked}
                disabled={readOnly || isDisabledByValidation || isAnswerReadOnly}
                onChange={(e) => {
                  // Handle checkbox state changes for multiple selection
                  const currentValues = formValues[questionId] || [];
                  const value = answer.answerId || answer.id;

                  if (e.target.checked) {
                    // Add value to selection if not already present
                    if (!currentValues.includes(value)) {
                      setFormValues(prev => ({
                        ...prev,
                        [questionId]: [...currentValues, value]
                      }));
                    }
                  } else {
                    // Remove value from selection
                    setFormValues(prev => ({
                      ...prev,
                      [questionId]: currentValues.filter(v => v !== value)
                    }));
                  }
                }}
              />
              <label
                className={`form-check-label ${(isDisabledByValidation || isAnswerReadOnly) ? 'text-muted' : ''}`}
                htmlFor={`checkbox-${questionId}-${answer.answerId || answer.id}`}
                style={{ opacity: (isDisabledByValidation || isAnswerReadOnly) ? 0.6 : 1 }}
                title={
                  isDisabledByValidation
                    ? t('optionDisabledByValidation', { ns: 'labels', defaultValue: 'Opzione disabilitata dalle regole di validazione' })
                    : isAnswerReadOnly
                      ? t('optionNotAssignedToYou', { ns: 'labels', defaultValue: 'Opzione non assegnata a te' })
                      : ''
                }
              >
                {getLocalizedText(answer.answerText, `${t('option', { ns: 'labels' })} ${aIndex + 1}`)}
                {isDisabledByValidation && (
                  <i className="fa-solid fa-lock ms-1 text-muted small"></i>
                )}
                {/* Show icon only if there's at least one editable answer (partial assignment) */}
                {isAnswerReadOnly && hasAtLeastOneEditableAnswer && (
                  <i className="fa-solid fa-user-lock ms-1 text-muted small" title={t('answerAssignedToOtherActor', { ns: 'labels', defaultValue: 'Risposta assegnata ad altro attore' })}></i>
                )}
                {showScores && formatScore && formatScore(answer.score) && (
                  <span style={{ color: 'black', marginLeft: '10px' }}>
                    ({t('score', { ns: 'labels' })}: {formatScore(answer.score)})
                  </span>
                )}
              </label>
            </div>
          );
        })}
      </div>
    );
  };

  /**
   * Renders a dropdown for vocabulary selection with multiple selection support
   * Shows selected items as a list below the dropdown (like groups in user management)
   * 
   * @returns {JSX.Element|null} Vocabulary dropdown or null if no vocabulary selected
   */
  const renderVocabularyDropdown = () => {
    if (!question.selectedVocabulary) return null;

    return (
      <div className="mt-3">
        {/* Dropdown per aggiungere vocabolari */}
        {!readOnly && (
          <div className="mb-3">
            <select
              className="form-select"
              onChange={(e) => {
                if (e.target.value) {
                  const currentValues = Array.isArray(formValues[questionId]) ? formValues[questionId] : [];
                  if (!currentValues.includes(e.target.value)) {
                    handleInputChange(questionId, [...currentValues, e.target.value]);
                  }
                  e.target.value = ""; // Reset selection
                }
              }}
            >
              <option value="">{t('selectVocabulary', { ns: 'labels' })}</option>
              {vocabularyItems[question.selectedVocabulary] && vocabularyItems[question.selectedVocabulary]
                .filter(item => {
                  const currentValues = Array.isArray(formValues[questionId]) ? formValues[questionId] : [];
                  return !currentValues.includes(item.Code);
                })
                .map(item => (
                  <option key={item.Code} value={item.Code}>
                    {item.Name}
                  </option>
                ))}
            </select>
          </div>
        )}
        
        {/* Lista degli elementi selezionati */}
        {(() => {
          const currentValues = Array.isArray(formValues[questionId]) ? formValues[questionId] : [];
          if (currentValues.length > 0) {
            return (
              <div className="list-group">
                {currentValues.map((code) => {
                  const vocItem = vocabularyItems[question.selectedVocabulary]?.find(item => item.Code === code);
                  if (!vocItem) return null;
                  
                  return (
                    <div key={code} className="list-group-item d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-bookmark text-primary me-3 fs-5"></i>
                        <div>
                          <h6 className="fs-14 mb-0 fw-semibold">{vocItem.Name}</h6>
                        </div>
                      </div>
                      {!readOnly && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            const updatedValues = currentValues.filter(v => v !== code);
                            handleInputChange(questionId, updatedValues);
                          }}
                          title={t('remove', { ns: 'labels', defaultValue: 'Remove' })}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          } else {
            return (
              <div className="alert alert-info">
                <p className="mb-0">{t('noVocabularySelected', { ns: 'labels', defaultValue: 'Nessun elemento selezionato' })}</p>
              </div>
            );
          }
        })()}
      </div>
    );
  };

  /**
   * Renders a dropdown for custom vocabulary selection with multiple selection support
   * Shows selected items as a list below the dropdown (like groups in user management)
   * 
   * @returns {JSX.Element|null} Custom vocabulary dropdown or null if no custom vocabulary selected
   */
  const renderCustomVocabularyDropdown = () => {
    if (!question.selectedCustomVocabulary) return null;

    return (
      <div className="mt-3">
        {/* Dropdown per aggiungere vocabolari personalizzati */}
        {!readOnly && (
          <div className="mb-3">
            <select
              className="form-select"
              onChange={(e) => {
                if (e.target.value) {
                  const currentValues = Array.isArray(formValues[questionId]) ? formValues[questionId] : [];
                  if (!currentValues.includes(e.target.value)) {
                    handleInputChange(questionId, [...currentValues, e.target.value]);
                  }
                  e.target.value = ""; // Reset selection
                }
              }}
            >
              <option value="">{t('selectCustomVocabulary', { ns: 'labels' }) || 'Seleziona Vocabolario Personalizzato'}</option>
              {customVocabularyItems[question.selectedCustomVocabulary] && customVocabularyItems[question.selectedCustomVocabulary]
                .filter(item => {
                  const currentValues = Array.isArray(formValues[questionId]) ? formValues[questionId] : [];
                  return !currentValues.includes(item.Code);
                })
                .map(item => (
                  <option key={item.Code} value={item.Code}>
                    {item.Name}
                  </option>
                ))}
            </select>
          </div>
        )}
        
        {/* Lista degli elementi selezionati */}
        {(() => {
          const currentValues = Array.isArray(formValues[questionId]) ? formValues[questionId] : [];
          if (currentValues.length > 0) {
            return (
              <div className="list-group">
                {currentValues.map((code) => {
                  const vocItem = customVocabularyItems[question.selectedCustomVocabulary]?.find(item => item.Code === code);
                  if (!vocItem) return null;
                  
                  return (
                    <div key={code} className="list-group-item d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-list text-primary me-3 fs-5"></i>
                        <div>
                          <h6 className="fs-14 mb-0 fw-semibold">{vocItem.Name}</h6>
                        </div>
                      </div>
                      {!readOnly && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            const updatedValues = currentValues.filter(v => v !== code);
                            handleInputChange(questionId, updatedValues);
                          }}
                          title={t('remove', { ns: 'labels', defaultValue: 'Remove' })}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          } else {
            return (
              <div className="alert alert-info">
                <p className="mb-0">{t('noCustomVocabularySelected', { ns: 'labels', defaultValue: 'Nessun vocabolario personalizzato selezionato' })}</p>
              </div>
            );
          }
        })()}
      </div>
    );
  };

  /**
   * Renders a table input for table-type questions
   * Supports various column types, row management, and validation
   * 
   * @returns {JSX.Element|null} Table input component or null if no columns configured
   */
  const renderTableInput = () => {
    const columns = question.tableColumns || [];
    const tableSettings = question.tableSettings || {};
    const currentTableData = formValues[questionId] || [];
    
    if (columns.length === 0) {
      return (
        <div className="alert alert-warning mt-3">
          <i className="fa fa-exclamation-triangle me-2"></i>
          {t('tableNoColumns', { ns: 'labels' })}
        </div>
      );
    }

    // Helper function to get localized column name
    const getColumnName = (column) => {
      return getLocalizedText(column.name, `Column ${columns.indexOf(column) + 1}`);
    };

    // Helper function to add a new row
    const addRow = () => {
      if (readOnly) return;
      
      const newRow = {};
      columns.forEach(col => {
        newRow[col.id] = '';
      });
      
      const updatedData = [...currentTableData, newRow];
      handleInputChange(questionId, updatedData);
    };

    // Helper function to delete a row
    const deleteRow = (rowIndex) => {
      if (readOnly) return;
      
      const updatedData = [...currentTableData];
      updatedData.splice(rowIndex, 1);
      handleInputChange(questionId, updatedData);
    };

    // Helper function to update cell value
    const updateCell = (rowIndex, columnId, value) => {
      if (readOnly) return;
      
      const updatedData = [...currentTableData];
      if (!updatedData[rowIndex]) {
        updatedData[rowIndex] = {};
      }
      updatedData[rowIndex][columnId] = value;
      handleInputChange(questionId, updatedData);
    };

    // Render table cell based on column type
    const renderTableCell = (row, column, rowIndex) => {
      const cellValue = row[column.id] || '';
      const cellId = `${questionId}_${rowIndex}_${column.id}`;
      
      switch (column.type) {
        case 'text':
          return (
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={column.placeholder || ''}
              value={cellValue}
              disabled={readOnly}
              onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
              required={column.required}
              minLength={column.validation?.minLength}
              maxLength={column.validation?.maxLength}
            />
          );
        case 'number':
          return (
            <input
              type="number"
              className="form-control form-control-sm"
              placeholder={column.placeholder || ''}
              value={cellValue}
              disabled={readOnly}
              onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
              required={column.required}
              min={column.validation?.min}
              max={column.validation?.max}
            />
          );
        case 'date':
          return (
            <input
              type="date"
              className="form-control form-control-sm"
              value={cellValue}
              disabled={readOnly}
              onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
              required={column.required}
            />
          );
        case 'time':
          return (
            <input
              type="time"
              className="form-control form-control-sm"
              value={cellValue}
              disabled={readOnly}
              onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
              required={column.required}
            />
          );
        case 'datetime':
          return (
            <input
              type="datetime-local"
              className="form-control form-control-sm"
              value={cellValue}
              disabled={readOnly}
              onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
              required={column.required}
            />
          );
        case 'select':
          return (
            <select
              className="form-select form-select-sm"
              value={cellValue}
              disabled={readOnly}
              onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
              required={column.required}
            >
              <option value="">{t('select', { ns: 'labels' })}</option>
              {column.options?.map((option, optIndex) => (
                <option key={optIndex} value={option.value}>
                  {getLocalizedText(option.text, option.value)}
                </option>
              ))}
            </select>
          );
        case 'customVocabulary':
          return (
            <Choices
              voc={(() => {
                // In compilation, customVocabulary items are provided in parent component props
                // Here, we expect question to carry selectedCustomVocabulary per column
                // We pass them through question context via compiler (FormQuestion props include customVocabularyItems)
                const selectedId = column.selectedCustomVocabulary;
                return selectedId ? (customVocabularyItems[selectedId] || []) : [];
              })()}
              value="Code"
              text="Name"
              className="inputStyle blackText bg-white"
              selected={cellValue ? [{ Code: cellValue }] : []}
              onChange={(e) => {
                const selectedValue = e.selected?.[0]?.Code || "";
                updateCell(rowIndex, column.id, selectedValue);
              }}
              placeholder={t('selectItem', { ns: 'labels' }) || 'seleziona elemento'}
              dropDownParent="root"
              allowClear={!column.required}
              disabled={readOnly}
            />
          );
        case 'period': {
          // Value stored as "start|end" ISO strings; if empty, ''
          const includeTime = !!column.periodIncludeTime;
          const [startVal, endVal] = (() => {
            if (typeof cellValue === 'string' && cellValue.includes('|')) {
              const [s, e] = cellValue.split('|');
              return [s || '', e || ''];
            }
            return ['', ''];
          })();

          const inputType = includeTime ? 'datetime-local' : 'date';

          const toComparable = (val) => {
            if (!val) return null;
            try {
              return new Date(val).getTime();
            } catch (e) {
              return null;
            }
          };

          const updatePeriod = (nextStart, nextEnd) => {
            // Validate start <= end when both present
            const s = toComparable(nextStart);
            const e = toComparable(nextEnd);
            if (s !== null && e !== null && s > e) {
              // Prevent invalid order: keep previous valid values, or clamp
              // Strategy: if updating start and it exceeds end, set end = start
              if (s > e) {
                const fixed = `${nextStart}|${nextStart}`;
                updateCell(rowIndex, column.id, fixed);
                return;
              }
            }
            updateCell(rowIndex, column.id, `${nextStart || ''}|${nextEnd || ''}`);
          };

          return (
            <div className="d-flex gap-2 align-items-center">
              <input
                type={inputType}
                className="form-control form-control-sm"
                value={startVal}
                disabled={readOnly}
                onChange={(e) => updatePeriod(e.target.value, endVal)}
                required={column.required}
              />
              <span className="text-muted small">{t('to', { ns: 'labels' })}</span>
              <input
                type={inputType}
                className="form-control form-control-sm"
                value={endVal}
                disabled={readOnly}
                onChange={(e) => updatePeriod(startVal, e.target.value)}
                required={column.required}
                min={!includeTime ? (startVal || undefined) : undefined}
              />
            </div>
          );
        }
        default:
          return (
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={column.placeholder || ''}
              value={cellValue}
              disabled={readOnly}
              onChange={(e) => updateCell(rowIndex, column.id, e.target.value)}
              required={column.required}
            />
          );
      }
    };

    return (
      <div className="table-responsive mt-3">
        <table className="table table-bordered table-sm">
          <thead>
            <tr>
              {tableSettings.showRowNumbers && (
                <th style={{ width: '60px' }}>{t('rowNumber', { ns: 'labels' })}</th>
              )}
              {columns.map((column) => (
                <th key={column.id}>
                  {getColumnName(column)}
                  {column.required && <span className="text-danger ms-1">*</span>}
                </th>
              ))}
              {tableSettings.allowDeleteRows && !readOnly && (
                <th style={{ width: '80px' }}>{t('tableAction', { ns: 'labels' })}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {currentTableData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (tableSettings.showRowNumbers ? 1 : 0) + (tableSettings.allowDeleteRows && !readOnly ? 1 : 0)} className="text-center text-muted">
                  {t('tableEmpty', { ns: 'labels' })}
                </td>
              </tr>
            ) : (
              currentTableData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {tableSettings.showRowNumbers && (
                    <td className="text-center">{rowIndex + 1}</td>
                  )}
                  {columns.map((column) => (
                    <td key={column.id}>
                      {renderTableCell(row, column, rowIndex)}
                    </td>
                  ))}
                  {tableSettings.allowDeleteRows && !readOnly && (
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => deleteRow(rowIndex)}
                        disabled={currentTableData.length <= (tableSettings.minRows || 0)}
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
        
        {/* Add Row Button */}
        {tableSettings.allowAddRows && !readOnly && (
          <div className="mt-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={addRow}
              disabled={currentTableData.length >= (tableSettings.maxRows || 999)}
            >
              <i className="fa fa-plus me-1"></i>
              {t('addRow', { ns: 'labels' })}
            </button>
          </div>
        )}
      </div>
    );
  };

  /**
   * Renders department and functional area selection dropdowns
   * Functional area options are filtered based on selected department
   * 
   * @returns {JSX.Element} Department/Functional Area selection interface
   */
  const renderDepartmentFunctionalAreaDropdowns = () => {
    return (
      <div className="mt-3">
        <div className="row">
          {/* Department selection */}
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">{t('department', { ns: 'labels' })}</label>
            <Choices
              voc={departmentsVoc || []}
              value="Id"
              text="Name"
              className="inputStyle blackText bg-white"
              selected={(() => {
                const currentValue = formValues[questionId];
                if (currentValue && currentValue.includes('|')) {
                  const departmentId = currentValue.split('|')[0];
                  const dept = departmentsVoc.find(d => d.Id === departmentId);
                  return dept ? [dept] : [];
                }
                return [];
              })()}
              onChange={(e) => {
                const selectedDepartmentId = e.selected?.[0]?.Id || "";
                
                // When department changes, clear the functional area selection
                // This forces the user to explicitly select a functional area
                const newValue = selectedDepartmentId ? `${selectedDepartmentId}|` : "";
                handleInputChange(questionId, newValue);
              }}
              placeholder={t('selectDepartment', { ns: 'labels' })}
              dropDownParent="root"
              allowClear={!isRequired}
              disabled={readOnly}
            />
          </div>
          
          {/* Functional area selection */}
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">{t('functionalArea', { ns: 'labels' })}</label>
            <Choices
              voc={(() => {
                // Filter functional areas based on selected department
                const currentValue = formValues[questionId];
                if (currentValue && currentValue.includes('|')) {
                  const departmentId = currentValue.split('|')[0];
                  if (departmentId) {
                    return getDescendants(functionalAreaVocs, departmentId);
                  }
                }
                return [];
              })()}
              value="Id"
              text="Name"
              className="inputStyle blackText bg-white"
              selected={(() => {
                const currentValue = formValues[questionId];
                if (currentValue && currentValue.includes('|')) {
                  const functionalAreaId = currentValue.split('|')[1];
                  const area = functionalAreaVocs.find(a => a.Id === functionalAreaId);
                  return area ? [area] : [];
                }
                return [];
              })()}
              onChange={(e) => {
                const selectedFunctionalAreaId = e.selected?.[0]?.Id || "";
                const currentValue = formValues[questionId] || "";
                const departmentId = currentValue.includes('|') ? currentValue.split('|')[0] : "";
                
                // Update the combined value (format: "departmentId|functionalAreaId")
                const newValue = departmentId ? `${departmentId}|${selectedFunctionalAreaId}` : "";
                handleInputChange(questionId, newValue);
              }}
              placeholder={t('selectFunctionalArea', { ns: 'labels' })}
              dropDownParent="root"
              allowClear={true}
              disabled={readOnly}
            />
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renders the appropriate input component based on question type
   * 
   * @returns {JSX.Element|null} Input component or null for unsupported types
   */
  const renderQuestionInput = () => {
    switch (question.questionType) {
      case 'open':
        return renderTextarea();
      case 'openNumber':
        return renderNumberInput();
      case 'date':
        return renderDateInput();
      case 'time':
        return renderTimeInput();
      case 'datetime':
        return renderDateTimeInput();
      case 'boolean':
      case 'yesNoNA':
      case 'single':
        return renderRadioButtons();
      case 'pdca':
      case 'multiple':
        return renderCheckboxes();
      case 'vocabulary':
        return renderVocabularyDropdown();
      case 'customVocabulary':
        return renderCustomVocabularyDropdown();
      case 'departmentFunctionalArea':
        return renderDepartmentFunctionalAreaDropdowns();
      case 'period':
        return renderPeriodInput();
      case 'table':
        return renderTableInput();
      default:
        return null;
    }
  };

  return (
    <div className="mb-4 pb-3" style={{ borderBottom: '1px solid #e9ecef' }}>
      {/* Question text with mandatory indicator */}
      <label className="form-label fw-bold fs-5">
        <EditorJSRenderer
          content={getLocalizedText(question.questionText, '')}
          language={question.language || 'it'}
          defaultText={`${t('question', { ns: 'labels' })} ${qIndex + 1}`}
        />
        {isRequired && <span className="text-danger ms-1">*</span>}
      </label>

      {/* Question description if present */}
      {question.description && (
        <div className="text-muted small mb-2">
          <EditorJSRenderer
            content={getLocalizedText(question.description, '')}
            language={question.language || 'it'}
            defaultText=""
          />
        </div>
      )}

      {/* Display attached files if any */}
      {renderAttachedFiles()}
      
      {/* Validation Rules - Show for multiple choice questions only (not in simulation mode) */}
      {!isSimulation && <QuestionValidationRules question={question} colors={colors} />}
      
      {/* Render question input based on type */}
      <div className="mt-3">
        {renderQuestionInput()}
      </div>

      {/* Display validation errors for multiple choice questions */}
      {(question.questionType === 'multiple' || question.questionType === 'pdca') && 
       validationErrors[questionId] && validationErrors[questionId].length > 0 && (
        <ValidationErrorDisplay 
          errors={validationErrors[questionId]} 
          colors={colors} 
        />
      )}

      {/* Attachment area for file uploads, links, observations, and text */}
      {renderAttachmentArea(question, questionId)}
    </div>
  );
};

export default FormQuestion; 